import axios from 'axios';
import { BinanceUSPosition, BinanceUSAccountBalance } from '../types/binanceus';

// Updated to point to the strategiz-core server
const SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

class BinanceUSService {
  private readonly apiKey: string;
  private readonly secretKey: string;
  private readonly userId: string | null;

  constructor({ apiKey, secretKey, userId = null }: { apiKey: string; secretKey: string; userId?: string | null }) {
    this.apiKey = apiKey;
    this.secretKey = secretKey;
    this.userId = userId;
  }

  /**
   * Test the connection to the BinanceUS API
   */
  async testConnection(): Promise<{ status: string; message: string }> {
    try {
      const response = await axios.get(`${SERVER_URL}/api/binanceus/test`);
      return response.data;
    } catch (error) {
      console.error('Error testing BinanceUS connection:', error);
      throw error;
    }
  }

  /**
   * Configure the BinanceUS API credentials
   */
  async configure(): Promise<{ status: string; message: string }> {
    try {
      if (!this.userId) {
        throw new Error('User ID is required for configuration');
      }

      const response = await axios.post(`${SERVER_URL}/api/binanceus/configure`, {
        apiKey: this.apiKey,
        secretKey: this.secretKey,
        userId: this.userId
      });

      return response.data;
    } catch (error) {
      console.error('Error configuring BinanceUS API:', error);
      throw error;
    }
  }

  /**
   * Get account balance using the server endpoint
   */
  async getAccountBalance(): Promise<BinanceUSAccountBalance> {
    try {
      // Validate API credentials before making the request
      if (!this.apiKey || !this.secretKey) {
        throw new Error('BinanceUS API Key and Secret Key are required for private endpoints');
      }

      let response;

      if (this.userId) {
        console.log(`Using userId endpoint for BinanceUS balance: ${this.userId}`);
        // Use the userId endpoint if available
        response = await axios.get(`${SERVER_URL}/api/binanceus/balance/${this.userId}`);
      } else {
        console.log('Using direct API key method for BinanceUS balance');
        // Otherwise, use the direct API key method
        response = await axios.post(`${SERVER_URL}/api/binanceus/balance`, {
          apiKey: this.apiKey,
          secretKey: this.secretKey
        });
      }

      if (response.data.status === 'error') {
        throw new Error(response.data.message || 'Error retrieving BinanceUS balance');
      }

      // Handle both the new format (with rawAccountData) and the old format
      if (response.data.rawAccountData) {
        console.log('Got raw Binance US data format, extracting positions');
        
        // Extract positions from the response
        return {
          totalUsdValue: response.data.totalUsdValue || response.data.totalValue || 0,
          positions: response.data.positions || []
        };
      } else {
        // Handle the old format
        return {
          totalUsdValue: response.data.totalUsdValue || 0,
          positions: response.data.positions || []
        };
      }
    } catch (error) {
      console.error('Error getting BinanceUS account balance:', error);
      throw error; // Rethrow the error to be handled by the caller
    }
  }

  /**
   * Get exchange information
   */
  async getExchangeInfo(): Promise<any> {
    try {
      const response = await axios.get(`${SERVER_URL}/api/binanceus/exchange-info`);
      return response.data;
    } catch (error) {
      console.error('Error getting BinanceUS exchange info:', error);
      throw error;
    }
  }

  /**
   * Get ticker prices
   */
  async getTickerPrices(): Promise<any> {
    try {
      const response = await axios.get(`${SERVER_URL}/api/binanceus/ticker-prices`);
      return response.data;
    } catch (error) {
      console.error('Error getting BinanceUS ticker prices:', error);
      throw error;
    }
  }

  /**
   * Get completely unmodified raw account data from the Binance US API
   * This returns the exact response from the Binance US API without any transformations
   */
  async getAccount(): Promise<any> {
    try {
      // Validate API credentials
      if (!this.apiKey || !this.secretKey) {
        throw new Error('BinanceUS API Key and Secret Key are required for private endpoints');
      }

      // Use the direct API endpoint to get raw account data
      const response = await axios.post(`${SERVER_URL}/api/binanceus/raw-account`, {
        apiKey: this.apiKey,
        secretKey: this.secretKey
      });

      // Return the completely unmodified raw data
      return response.data;
    } catch (error) {
      console.error('Error getting raw BinanceUS account data:', error);
      throw error;
    }
  }

  /**
   * Get raw account data for a specific user
   * This is used for admin purposes to view the completely unmodified data
   */
  async getRawAccountData(): Promise<any> {
    try {
      if (!this.userId) {
        throw new Error('User ID is required to get raw account data');
      }

      const response = await axios.get(`${SERVER_URL}/api/binanceus/raw-data/${this.userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting raw BinanceUS account data for user:', error);
      throw error;
    }
  }

  /**
   * Check if the server is running
   */
  static async isServerRunning(): Promise<boolean> {
    try {
      await axios.head(`${SERVER_URL}/api/binanceus`);
      return true;
    } catch (error) {
      console.warn('BinanceUS server is not running:', error);
      return false;
    }
  }
}

export const binanceusService = {
  createInstance: (config: { apiKey: string; secretKey: string; userId?: string | null }) => {
    return new BinanceUSService(config);
  },
  isServerRunning: BinanceUSService.isServerRunning
};
